import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { TESTIMONIALS } from "@/data/testimonials";

const initials = (t) =>
  (t.name || t.company || "?")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Avatar = ({ t }) =>
  t.avatarUrl ? (
    <img
      src={t.avatarUrl}
      alt={`${t.company} logo`}
      className="w-16 h-16 rounded-full object-cover border-2 border-crimson/30 shadow-[0_10px_30px_rgba(255,42,59,0.25)]"
    />
  ) : (
    <span className="w-16 h-16 rounded-full bg-gradient-to-br from-crimson to-gold flex items-center justify-center font-display font-extrabold text-white text-lg shadow-[0_10px_30px_rgba(255,42,59,0.3)]">
      {initials(t)}
    </span>
  );

export default function Testimonials() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 7000);
    return () => clearInterval(t);
  }, []);

  const pick = (i) => setIdx(((i % TESTIMONIALS.length) + TESTIMONIALS.length) % TESTIMONIALS.length);
  const item = TESTIMONIALS[idx];

  return (
    <section id="reviews" className="py-24 md:py-32 scroll-mt-20">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="font-mono text-[11px] tracking-[0.25em] text-crimson uppercase mb-4">Testimonials</div>
          <h2 className="font-display font-extrabold text-gray-900 text-3xl md:text-5xl tracking-tight">
            Hear What They Say <span className="text-crimson">About Us</span>
          </h2>
        </div>

        <div className="relative" data-testid="testimonials-carousel">
          <AnimatePresence mode="wait">
            <motion.figure
              key={idx}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-3xl border border-gray-100 bg-white shadow-soft px-8 py-12 md:px-16 md:py-14 text-center"
              data-testid="testimonial-card"
            >
              <Quote className="w-8 h-8 text-crimson mx-auto mb-6 fill-crimson/10" />
              <blockquote className="text-gray-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                “{item.quote}”
              </blockquote>
              {item.result && (
                <div
                  className="inline-block mt-6 px-4 py-1.5 rounded-full bg-crimson/5 border border-crimson/25 font-mono text-[11px] tracking-widest text-crimson uppercase"
                  data-testid="testimonial-result-line"
                >
                  {item.result}
                </div>
              )}
              <figcaption className="mt-8 flex flex-col items-center gap-3">
                <Avatar t={item} />
                <span className="font-display font-bold text-gray-900">
                  {item.name} <span className="text-gray-400 font-medium">— {item.role}</span>
                </span>
              </figcaption>
            </motion.figure>
          </AnimatePresence>

          <button
            type="button"
            data-testid="testimonial-prev-button"
            onClick={() => pick(idx - 1)}
            aria-label="Previous testimonial"
            className="absolute -left-4 md:-left-16 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-gray-200 shadow-soft flex items-center justify-center text-gray-600 hover:text-crimson hover:border-crimson transition-colors duration-300"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            data-testid="testimonial-next-button"
            onClick={() => pick(idx + 1)}
            aria-label="Next testimonial"
            className="absolute -right-4 md:-right-16 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-gray-200 shadow-soft flex items-center justify-center text-gray-600 hover:text-crimson hover:border-crimson transition-colors duration-300"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="flex items-center justify-center gap-2 mt-7">
            {TESTIMONIALS.map((t, i) => (
              <button
                key={t.company}
                type="button"
                data-testid={`testimonial-dot-${i}`}
                onClick={() => pick(i)}
                aria-label={`Testimonial ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-crimson" : "w-2 bg-gray-300 hover:bg-gray-400"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
